
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, UserPlus, User, Check, ArrowUp, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Activity {
  id: string;
  type: 'payment' | 'member_added' | 'plan_changed' | 'account_activated';
  title: string;
  description: string;
  timestamp: string;
  status?: 'success' | 'pending' | 'failed';
}

const ActivityFeed: React.FC = () => {
  // Données fictives pour la démo
  const activities: Activity[] = [
    {
      id: '1',
      type: 'payment',
      title: 'Paiement traité',
      description: 'Paiement de €29.00 pour le plan Premium',
      timestamp: '2023-11-01T10:23:00',
      status: 'success',
    },
    {
      id: '2',
      type: 'member_added',
      title: 'Nouveau membre',
      description: 'Jane Smith a rejoint votre équipe',
      timestamp: '2023-10-25T14:12:00',
    },
    {
      id: '3',
      type: 'plan_changed',
      title: 'Plan mis à jour',
      description: 'Upgrade du plan Starter vers Premium',
      timestamp: '2023-10-15T09:45:00',
    },
    {
      id: '4',
      type: 'account_activated',
      title: 'Compte activé',
      description: 'Votre compte est maintenant actif',
      timestamp: '2023-10-01T08:30:00',
    },
    {
      id: '5',
      type: 'payment',
      title: 'Paiement en attente',
      description: 'Paiement de €29.00 pour le plan Premium',
      timestamp: '2023-09-01T10:00:00',
      status: 'pending',
    },
  ];
  
  const getActivityIcon = (type: string, status?: string) => {
    switch (type) {
      case 'payment':
        if (status === 'success') return <CreditCard className="h-8 w-8 text-green-500 p-1.5 bg-green-100 rounded-full" />;
        if (status === 'pending') return <Clock className="h-8 w-8 text-yellow-500 p-1.5 bg-yellow-100 rounded-full" />;
        if (status === 'failed') return <CreditCard className="h-8 w-8 text-red-500 p-1.5 bg-red-100 rounded-full" />;
        return <CreditCard className="h-8 w-8 text-gray-500 p-1.5 bg-gray-100 rounded-full" />;
      case 'member_added':
        return <UserPlus className="h-8 w-8 text-blue-500 p-1.5 bg-blue-100 rounded-full" />;
      case 'plan_changed':
        return <ArrowUp className="h-8 w-8 text-purple-500 p-1.5 bg-purple-100 rounded-full" />;
      case 'account_activated':
        return <Check className="h-8 w-8 text-green-500 p-1.5 bg-green-100 rounded-full" />;
      default:
        return <User className="h-8 w-8 text-gray-500 p-1.5 bg-gray-100 rounded-full" />;
    }
  };
  
  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    
    switch (status) {
      case 'success':
        return <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">Réussi</Badge>;
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">En attente</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-100 text-red-800 hover:bg-red-100">Échoué</Badge>;
      default:
        return null;
    }
  };
  
  // Formater la date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return `Il y a ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
      }
      return `Il y a ${diffInHours} heure${diffInHours !== 1 ? 's' : ''}`;
    } else if (diffInDays === 1) {
      return 'Hier';
    } else if (diffInDays < 7) {
      return `Il y a ${diffInDays} jour${diffInDays !== 1 ? 's' : ''}`;
    } else {
      const options: Intl.DateTimeFormatOptions = { 
        day: 'numeric', 
        month: 'short',
      };
      return date.toLocaleDateString('fr-FR', options);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activité récente</CardTitle>
        <CardDescription>
          Historique des dernières activités de votre compte
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {activities.map((activity) => (
            <div key={activity.id} className="flex">
              <div className="mr-4">
                {getActivityIcon(activity.type, activity.status)}
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium leading-none">{activity.title}</p>
                  {getStatusBadge(activity.status)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {activity.description}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(activity.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
